import * as React from 'react';
import { AppBar, TitlePortal } from 'react-admin';
import { Box, useMediaQuery, Theme, alpha } from '@mui/material';

import Logo from './Logo';
import { AppBarToolbar } from './AppBarToolbar';
import { appBarColors } from './appBarTheme';

/**
 * The top bar.
 *
 * Every theme in common/themes paints this bar flat white or flat grey, which
 * is why it read as a strip of nothing above much richer screens. It is now a
 * gradient drawn from the ACTIVE theme's own primary and secondary colours --
 * not from fixed hex values -- so it stays a deliberate brand band whichever of
 * the six themes is picked, and in dark mode, instead of becoming a bright
 * island the first time somebody changes the palette. Foreground colour is
 * derived, not assumed: see appBarTheme.ts.
 *
 * The same gradient recipe is used by the dashboard hero header
 * (screens/dashboard/schoolDashboard.tsx), so the bar and the page below it
 * read as one surface rather than two unrelated designs stacked up.
 */
const CustomAppBar = () => {
    const isLargeEnough = useMediaQuery<Theme>(theme => theme.breakpoints.up('sm'));

    return (
        <AppBar
            color="secondary"
            elevation={0}
            toolbar={<AppBarToolbar />}
            sx={theme => {
                const { from, mid, to, fg } = appBarColors(theme);
                // Nested under '&&' on purpose. Several of the bundled
                // themes set MuiAppBar.colorSecondary themselves --
                // radiantTheme forces it to background.default -- and a
                // single-class sx loses that race, which left the bar pale
                // grey with a white logo on it. Doubling the selector
                // outranks the theme override without resorting to
                // !important.
                return {
                    '&&': {
                        color: fg,
                        backgroundColor: mid,
                        backgroundImage: `linear-gradient(100deg, ${from} 0%, ${mid} 55%, ${alpha(
                            to,
                            0.9
                        )} 100%)`,
                        borderBottom: `1px solid ${alpha(fg, 0.14)}`,
                        boxShadow: `0 2px 14px ${alpha(from, 0.35)}`,
                    },

                    // A soft highlight in the top-left corner, so the band has
                    // some depth rather than being a flat sweep of colour.
                    '&::before': {
                        content: '""',
                        position: 'absolute',
                        inset: 0,
                        background: `radial-gradient(circle at 12% -40%, ${alpha(
                            fg,
                            0.18
                        )}, transparent 60%)`,
                        pointerEvents: 'none',
                    },

                    // Everything the bar hosts -- the burger, the title portal,
                    // the user menu -- takes the derived foreground rather than
                    // each one needing its own colour prop.
                    // No minHeight here. The layout reserves a fixed offset for
                        // the bar and does not measure it, so making the bar
                        // taller pushed it over the top of the page content.
                        '& .MuiToolbar-root': { position: 'relative', zIndex: 1 },
                    '& .RaAppBar-toolbar, & .MuiIconButton-root, & .MuiButton-root': { color: fg },
                    '& .RaAppBar-title': { fontWeight: 700 },
                };
            }}
        >
            {isLargeEnough && <Logo />}
            <Box component="span" sx={{ flex: 1 }} />
            <TitlePortal
                sx={{ flex: 'unset', fontWeight: 700, letterSpacing: '.01em', textAlign: 'center' }}
            />
            <Box component="span" sx={{ flex: 1 }} />
        </AppBar>
    );
};

export default CustomAppBar;
